import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import Footer from '../components/Footer';
import { 
  ArrowLeft, Send, Gamepad2, Bot, User, Mail, MessageSquare,
  Loader2, Sparkles, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [mode, setMode] = useState('form'); // 'form' or 'chat'
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  
  // AI Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hi! I'm PlayMold's AI assistant. How can I help you today? I can answer questions about features, pricing, technical issues, and more!" }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      // Post to support inbox — admins can reply from Admin Dashboard.
      // Payload matches /api/support/message: {name, email, message}
      await axios.post(`${API}/support/message`, {
        name: formData.name,
        email: formData.email,
        message: `${formData.subject}\n\n${formData.message}`,
      });
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMsg = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await axios.post(`${API}/support/ai-chat`, {
        message: chatInput,
        history: chatMessages.slice(-10)
      });

      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I'm having trouble connecting. Please try the contact form or email support@playmold.com (delivered internally to bloomraynexus@gmail.com)." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0C]/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center">
              <Gamepad2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">PlayMold</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-[#06B6D4]/20 flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-8 h-8 text-[#06B6D4]" />
            </div>
            <h1 className="text-4xl font-black text-white mb-4">
              Contact Support
            </h1>
            <p className="text-lg text-zinc-400">
              Get help from our AI assistant or reach our support team
            </p>
          </div>

          {/* Mode Toggle */}
          <div className="flex justify-center gap-2 mb-8">
            <Button
              variant={mode === 'chat' ? 'default' : 'outline'}
              onClick={() => setMode('chat')}
              className={mode === 'chat' ? 'bg-[#06B6D4] text-black' : 'border-[#1E1E24]'}
            >
              <Bot className="w-4 h-4 mr-2" />
              AI Assistant
            </Button>
            <Button
              variant={mode === 'form' ? 'default' : 'outline'}
              onClick={() => setMode('form')}
              className={mode === 'form' ? 'bg-[#06B6D4] text-black' : 'border-[#1E1E24]'}
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Form
            </Button>
          </div>

          {mode === 'chat' ? (
            /* AI Chat Interface */
            <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-[#1E1E24] flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#06B6D4]/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#06B6D4]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">PlayMold AI Assistant</h3>
                  <p className="text-xs text-zinc-500">Ask me anything about PlayMold</p>
                </div>
              </div>

              <ScrollArea className="h-[400px] p-4">
                <div className="space-y-4">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        msg.role === 'user' ? 'bg-[#06B6D4]' : 'bg-[#1E1E24]'
                      }`}>
                        {msg.role === 'user' ? (
                          <User className="w-4 h-4 text-black" />
                        ) : (
                          <Bot className="w-4 h-4 text-[#06B6D4]" />
                        )}
                      </div>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user' 
                          ? 'bg-[#06B6D4] text-black' 
                          : 'bg-[#1E1E24] text-zinc-300'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1E1E24] flex items-center justify-center">
                        <Bot className="w-4 h-4 text-[#06B6D4]" />
                      </div>
                      <div className="bg-[#1E1E24] rounded-2xl px-4 py-3">
                        <Loader2 className="w-5 h-5 text-[#06B6D4] animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>

              <div className="p-4 border-t border-[#1E1E24] flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Ask a question..."
                  className="bg-[#1E1E24] border-[#2E2E34] text-white"
                />
                <Button 
                  onClick={sendChatMessage} 
                  disabled={chatLoading}
                  className="bg-[#06B6D4] text-black"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            /* Contact Form */
            <form onSubmit={handleFormSubmit} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="bg-[#1E1E24] border-[#2E2E34] text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="bg-[#1E1E24] border-[#2E2E34] text-white"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-400 mb-2">Subject</label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="What is this about?"
                  className="bg-[#1E1E24] border-[#2E2E34] text-white"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-400 mb-2">Message *</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your issue or question..."
                  required
                  rows={6}
                  className="bg-[#1E1E24] border-[#2E2E34] text-white resize-none"
                />
              </div>

              <Button type="submit" disabled={sending} className="w-full bg-[#06B6D4] text-black">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </form>
          )}

          {/* Quick Links */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/faq')}
              className="p-4 bg-[#0A0A0C] border border-[#1E1E24] rounded-xl text-left hover:border-[#06B6D4]/50 transition-colors"
            >
              <HelpCircle className="w-6 h-6 text-[#06B6D4] mb-2" />
              <h4 className="font-semibold text-white">FAQ</h4>
              <p className="text-sm text-zinc-500">Find quick answers</p>
            </button>
            <button
              onClick={() => navigate('/tutorial')}
              className="p-4 bg-[#0A0A0C] border border-[#1E1E24] rounded-xl text-left hover:border-[#06B6D4]/50 transition-colors"
            >
              <Sparkles className="w-6 h-6 text-[#06B6D4] mb-2" />
              <h4 className="font-semibold text-white">Tutorial</h4>
              <p className="text-sm text-zinc-500">Learn how to use PlayMold</p>
            </button>
            <a
              href="mailto:support@playmold.com"
              className="p-4 bg-[#0A0A0C] border border-[#1E1E24] rounded-xl text-left hover:border-[#06B6D4]/50 transition-colors"
            >
              <Mail className="w-6 h-6 text-[#06B6D4] mb-2" />
              <h4 className="font-semibold text-white">Email</h4>
              <p className="text-sm text-zinc-500">support@playmold.com</p>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
